<script setup>
import {Redirect} from "@shopify/app-bridge/actions";
import useShopify from "@/composables/shopify/useShopify";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, ref, watch} from "vue";
import CustomRadioInput from "@/components/global-themed/CustomRadioInput.vue";

const props = defineProps({
  host: {
    type: String,
    required: true
  },
  subscriptionOptionsJson: {
    type: String,
    required: true,
  }
});

const {app, baseUrl} = useShopify(props.host);

const redirect = Redirect.create(app);

const selectedPrice = ref();

const subscriptionOptions = computed(() => JSON.parse(props.subscriptionOptionsJson));

const goToPayment = () => {
  redirect.dispatch(Redirect.Action.REMOTE, `${baseUrl}/shopify/subscription/create?price_id=${selectedPrice.value.id}`);
};

watch(
  subscriptionOptions,
  (newOptions) => {
    if (newOptions && newOptions[0] && !selectedPrice.value) {
      selectedPrice.value = newOptions[0];
    }
  },
  { immediate: true }
);

const features = computed(() => [
  {
    title: 'Full access to the website SEO toolset',
    enabled: true,
  },
  {
    title: 'Full access to Google Business Profile toolset',
    enabled: true,
  },
  {
    title: '14-day money-back guarantee',
    enabled: true,
  },
  {
    title: 'Save 2 months with a yearly subscription.',
    enabled: selectedPrice.value?.billing_period === 'YEAR',
  },
]);

</script>

<template>
  <div class="tw-flex tw-justify-center tw-items-center tw-min-h-full tw-w-full tw-flex-col">
    <div class="tw-flex tw-max-w-[60rem] tw-w-full tw-m-8 tw-gap-6">
      <div class="tw-flex tw-flex-col tw-gap-6 tw-w-full tw-grow">
        <div class="tw-flex tw-flex-col tw-gap-6">
          <h2 class="tw-text-2xl tw-font-semibold tw-text-neutral-900">
            Selected Plan
          </h2>

          <div class="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8">
            <span class="tw-text-2xl tw-font-semibold tw-text-neutral-900">
              Hike + Kit
            </span>
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-6">
          <h2 class="tw-text-2xl tw-font-bold tw-text-neutral-900">
            Billing Frequency
          </h2>

          <div class="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-flex tw-flex-col tw-gap-4 tw-text-neutral-900">
            <CustomRadioInput
              v-for="option in subscriptionOptions"
              :id="`price_${option.id}`"
              :key="option.id"
              v-model="selectedPrice"
              name="price"
              class="tw-rounded-lg tw-flex tw-p-4 tw-gap-4 tw-border tw-border-neutral-300 tw-items-center tw-cursor-pointer tw-transition-all tw-duration-300"
              :class="{
                'tw-border-primary': option.id === selectedPrice?.id,
              }"
              :true-value="option"
            >
              <div class="tw-font-semibold">
                Pay {{ option.billing_period === 'MONTH' ? 'Monthly' : 'Yearly' }}
              </div>

              <div
                v-if="option.billing_period === 'YEAR'"
                class="tw-rounded-full tw-border tw-border-success-600 tw-bg-success-100 tw-text-success-900 tw-py-1 tw-px-2 tw-text-xxs tw-font-semibold"
              >
                Save 20%
              </div>

              <div class="tw-ml-auto">
                <span
                  class="tw-font-semibold tw-text-xl tw-text-neutral-900"
                  v-html="option.price.ex_vat_display"
                />
                <span class="tw-text-neutral tw-text-xs">
                  {{ option.billing_period === 'MONTH' ? '/Month' : '/Year' }}
                </span>
              </div>
            </CustomRadioInput>
          </div>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-6 tw-w-[45rem] tw-max-w-full tw-grow-0 tw-min-h-full">
        <h2 class="tw-text-2xl tw-font-semibold tw-text-neutral-900">
          Order Summary
        </h2>

        <div class="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-flex tw-flex-col tw-gap-4 tw-h-full tw-grow">
          <div class="tw-flex tw-justify-between tw-gap-8">
            <h3 class="tw-text-xl tw-text-neutral-900 tw-font-semibold">
              Total To Pay Now
            </h3>
            <span
              v-if="selectedPrice"
              class="tw-text-xl tw-text-neutral-900 tw-font-semibold"
              v-html="selectedPrice.price.ex_vat_display"
            />
          </div>

          <hr>

          <div class="tw-flex tw-flex-col tw-gap-4">
            <div
              v-for="feature in features"
              :key="feature.title"
              class="tw-flex tw-gap-4"
            >
              <div
                class="tw-w-6 tw-h-6 tw-p-2 tw-rounded-full tw-transition-all tw-duration-300"
                :class="{
                  'tw-bg-primary': feature.enabled,
                  'tw-bg-neutral-300': !feature.enabled,
                }"
              >
                <svg-vue
                  v-show="feature.enabled"
                  icon="icons/check"
                  class="tw-w-full tw-h-full tw-text-white tw-transition-all tw-duration-300"
                />
              </div>

              <span
                class="tw-block tw-transition-all tw-duration-300"
                :class="{
                  'tw-text-neutral-900': feature.enabled,
                  'tw-text-neutral-400 tw-line-through': !feature.enabled
                }"
              >
                {{ feature.title }}
              </span>
            </div>
          </div>

          <hr>

          <DefaultButton
            :disabled="!selectedPrice"
            class="tw-w-full tw-mt-auto"
            @click="goToPayment"
          >
            Complete Purchase Now
          </DefaultButton>
        </div>
      </div>
    </div>
  </div>
</template>
